import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { useUser } from "../../contexts/UserContext";
import { useDashboardData } from "../../hooks/useDashboardData";
import { useNavigate } from "react-router-dom";
import { formatSchoolId } from "../../utils/formatUtils";
import MessagesList from '../shared/MessagesList';
import StudentMessaging from '../shared/StudentMessaging';
import useStudentDashboard from '../../hooks/useStudentDashboard';
import MessageModal from '../shared/MessageModal';
import CallManager from '../shared/CallManager';
import CallService from '../../services/callService';
import CallHistoryService, { CallRecord } from '../../services/callHistoryService';
import { FaPhone, FaPhoneSlash, FaArrowDown, FaArrowUp, FaExclamationCircle } from 'react-icons/fa';
import EmergencyButton from '../EmergencyButton';
import { api } from '../../utils/api';

// Navigation and layout styled components
const PageContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: #f8fafc;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Sidebar = styled.aside`
  width: 250px;
  background: linear-gradient(180deg, #7e22ce 0%, #6b21a8 100%);
  color: white;
  padding: 1.5rem 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 1rem 0;
  }
`;

const Logo = styled.div`
  padding: 0 1.5rem 1.5rem;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 1.5rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const NavMenu = styled.nav`
  margin-top: 1.5rem;
`;

const NavItem = styled.a`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.5rem;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover, &.active {
    background-color: rgba(255, 255, 255, 0.1);
    color: white;
  }
  
  &.active {
    border-left: 3px solid white;
  }
`;

const ContentArea = styled.main`
  flex: 1;
  background-color: #f8fafc;
  overflow-y: auto;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background-color: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  position: sticky;
  top: 0;
  z-index: 10;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const SearchBar = styled.div`
  position: relative;
  width: 300px;
  
  @media (max-width: 768px) {
    width: 100%;
    max-width: 300px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem 1rem 0.5rem 2.5rem;
  border-radius: 9999px;
  border: 1px solid #e2e8f0;
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
    border-color: #7e22ce;
    box-shadow: 0 0 0 2px rgba(126, 34, 206, 0.2);
  }
`;

const UserControls = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 9999px;
  position: relative;
  
  &:hover {
    background-color: #f1f5f9;
    color: #334155;
  }
`;

const NotificationBadge = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  background-color: #ef4444;
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 9999px;
`;

const ProfileButton = styled.button`
  background: none;
  border: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  padding: 0.5rem 0.75rem;
  border-radius: 0.375rem;
  
  &:hover {
    background-color: #f1f5f9;
  }
`;

const Avatar = styled.div`
  width: 2rem;
  height: 2rem;
  border-radius: 9999px;
  background-color: #7e22ce;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.875rem;
`;

// Dashboard styled components (enhanced from original)
const DashboardContainer = styled.div`
  max-width: 100vw;
  margin: 0 auto;
  padding: 0.5rem 0.5vw;
`;

const Header = styled.header`
  margin-bottom: 1rem;
  padding: 1rem;
  border-radius: 0.75rem;
  background: linear-gradient(135deg, #7e22ce 0%, #6b21a8 100%);
  color: white;
  box-shadow: 0 2px 6px rgba(126, 34, 206, 0.15);
  position: relative;
`;

const EditProfileButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: #f1f5f9; /* Light grey background */
  color: #334155; /* Dark grey text */
  border: 1px solid #e2e8f0; /* Light grey border */
  margin-right: 3rem; /* Increase right margin significantly */
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    background-color: #e2e8f0; /* Slightly darker grey on hover */
  }
`;

const DashboardTitle = styled.h2`
  color: white;
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
`;

const WelcomeText = styled.p`
  font-size: 1rem;
  margin-bottom: 0.5rem;
  color: #f3e8ff;
  font-weight: 600;
`;

const UserInfo = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  color: #f3e8ff;
  font-size: 0.95rem;
  span {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    background: rgba(255, 255, 255, 0.1);
    padding: 0.25rem 0.75rem;
    border-radius: 1.5rem;
    backdrop-filter: blur(2px);
  }
  strong {
    color: #f3e8ff;
    font-weight: 600;
  }
`;

const SectionGrid = styled.div`
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  margin: 0;
`;

const Section = styled.section`
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  padding: 0.75rem;
  height: 100%;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  }
`;

const SectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const StyledList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
`;

const ListItem = styled.li`
  padding: 0.75rem 0;
  border-bottom: 1px solid #f1f5f9;
  
  &:last-child {
    border-bottom: none;
  }
`;

const NotificationItem = styled(ListItem)`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: 0.375rem;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: #f8fafc;
  }
  
  &:before {
    content: "";
    min-width: 0.5rem;
    height: 0.5rem;
    border-radius: 9999px;
    background-color: #7e22ce;
    margin-top: 0.5rem;
  }
`;

const StatContainer = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-top: 1rem;
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #f8fafc;
  border-radius: 0.5rem;
  padding: 1.25rem;
  flex: 1;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    background-color: #f1f5f9;
  }
`;

const StatValue = styled.span`
  font-size: 1.75rem;
  font-weight: 700;
  color: #7e22ce;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.span`
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
`;

const ErrorText = styled.p`
  color: #ef4444;
  padding: 1.5rem;
  text-align: center;
  background-color: #fee2e2;
  border-radius: 0.75rem;
  margin: 1.5rem;
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 3rem;
  color: #64748b;
`;

const ErrorContainer = styled.div`
  color: #ef4444;
  padding: 1.5rem;
  text-align: center;
  background-color: #fee2e2;
  border-radius: 0.75rem;
  margin: 1.5rem;
`;

// SVG Icons
const SearchIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const BellIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const ScheduleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const ActivityIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);

const PerformanceIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 20V10" />
    <path d="M12 20V4" />
    <path d="M6 20v-6" />
  </svg>
);

const CrowdfundingIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

const NotificationsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const RecommendationsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
  </svg>
);

interface ScheduleSlot {
  time: string;
  subject: string;
}

interface Activity {
  detail: string;
}

interface Performance {
  learningStreak: string;
  averageScore: string;
}

interface Crowdfunding {
  status: string;
  appliedOn: string;
}

interface Notification {
  message: string;
}

interface Recommendation {
  subject: string;
  suggestion: string;
}

interface DashboardData {
  schedule: ScheduleSlot[];
  recentActivity: Activity[];
  performance: Performance;
  crowdfunding: Crowdfunding[];
  notifications: Notification[];
  recommendations: Recommendation[];
  loading: boolean;
  error: string | null;
}

// Base student interface
interface BaseStudent {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  grade: string;
}

// Interface for students from other schools
interface OtherSchoolStudent extends BaseStudent {
  schoolName: string;
}

// Interface for schoolmates
interface Schoolmate extends BaseStudent {
  school: string;
}

// Interface for raw student data from the API
interface RawStudent extends BaseStudent {
  school?: string;
}

interface OtherSchoolsResponse {
  success: boolean;
  data: Record<string, Record<string, OtherSchoolStudent[]>>;
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalStudents: number;
    hasMore: boolean;
  };
}

// Add new interfaces
interface StudentProfile {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  grade: string;
  schoolName: string;
  biography?: string;
  strengths?: string[];
  likes?: string[];
  hobbies?: string[];
  profilePhoto?: string;
  careerAspiration?: string;
  cbcCompetencies?: string[];
  skills?: string[];
  extracurricularActivities?: string[];
  projects?: { name: string; description: string; url?: string; competenciesCovered?: string[] }[];
}

// Add new styled components
const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ViewProfileButton = styled.button`
  background: #4b5563;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: #374151;
  }
`;

const ProfileModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ProfileModalContent = styled.div`
  background: white;
  border-radius: 0.5rem;
  padding: 2rem;
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  
  &:hover {
    color: #374151;
  }
`;

const ProfileHeader = styled.div`
  display: flex;
  gap: 2rem;
  margin-bottom: 2rem;
`;

const ProfilePhoto = styled.img<{
  // Remove photoUrl prop as it's not needed for styled.img
}>`
  width: 150px;
  height: 150px;
  border-radius: 50%;
  object-fit: cover; // Use object-fit instead of background-size
  border: 3px solid #7e22ce;
`;

const ProfileInfo = styled.div`
  flex: 1;
`;

const ProfileName = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.5rem;
`;

const ProfileSchool = styled.p`
  color: #6b7280;
  margin-bottom: 1rem;
`;

const ProfileSection = styled.div`
  margin-bottom: 1.5rem;
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Tag = styled.span`
  background: #f3f4f6;
  color: #4b5563;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.875rem;
`;

// Add new styled components for EditProfileModal
const EditProfileModal = styled(ProfileModal)``;

const EditProfileContent = styled(ProfileModalContent)`
  max-width: 800px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #374151;
`;

const Input = styled.input`
  padding: 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: #7e22ce;
    box-shadow: 0 0 0 2px rgba(126, 34, 206, 0.2);
  }
`;

const TextArea = styled.textarea`
  padding: 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  min-height: 100px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #7e22ce;
    box-shadow: 0 0 0 2px rgba(126, 34, 206, 0.2);
  }
`;

const TagInput = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  min-height: 2.5rem;

  &:focus-within {
    border-color: #7e22ce;
    box-shadow: 0 0 0 2px rgba(126, 34, 206, 0.2);
  }
`;

const TagInputField = styled.input`
  border: none;
  outline: none;
  flex: 1;
  min-width: 120px;
  font-size: 0.875rem;
`;

const PhotoUploadSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  border: 2px dashed #e5e7eb;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #7e22ce;
    background-color: #faf5ff;
  }
`;

const UploadButton = styled.button`
  background: #7e22ce;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: #6b21a8;
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

const SaveButton = styled(UploadButton)`
  align-self: flex-end;
  margin-top: 1rem;
`;

const DeletePhotoButton = styled.button`
  background: #ef4444;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: #dc2626;
  }
`;

const ToggleButton = styled.button`
  background: #7e22ce;
  color: white;
  border: none;
  border-radius: 0.5rem;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover {
    background: #6b21a8;
  }

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const ActionButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: none;

  &.primary {
    background: #7e22ce;
    color: white;

    &:hover {
      background: #6b21a8;
    }
  }

  &.secondary {
    background: #f3f4f6;
    color: #4b5563;

    &:hover {
      background: #e5e7eb;
    }
  }
`;

const MessageButton = styled(ActionButton)`
  background: #7e22ce;
  color: white;

  &:hover {
    background: #6b21a8;
  }
`;

const CallButton = styled(ActionButton)`
  background: #10b981;
  color: white;

  &:hover {
    background: #059669;
  }
`;

// Move all styled-components here (before MainDashboard)
const MessageSection = styled.div`
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow: hidden;
`;
const MessageHeader = styled.div`
  padding: 1.25rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const MessageTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;
const MessageTabs = styled.div`
  display: flex;
  gap: 0.5rem;
  padding: 0 1.25rem;
  border-bottom: 1px solid #e5e7eb;
`;
const MessageTab = styled.button<{ active?: boolean }>`
  padding: 0.75rem 1rem;
  border: none;
  background: none;
  color: ${props => props.active ? '#7e22ce' : '#6b7280'};
  font-weight: ${props => props.active ? '600' : '500'};
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;

  &:after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 2px;
    background: ${props => props.active ? '#7e22ce' : 'transparent'};
    transition: all 0.2s ease;
  }

  &:hover {
    color: #7e22ce;
  }
`;
const MessageContent = styled.div`
  padding: 1.25rem;
`;
const MessageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-height: 500px;
  overflow-y: auto;
  padding-right: 0.5rem;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`;
const MessageItem = styled.div<{ isUnread?: boolean }>`
  padding: 1rem;
  border-radius: 0.5rem;
  background: ${props => props.isUnread ? '#f3f4f6' : 'white'};
  border: 1px solid #e5e7eb;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    border-color: #7e22ce;
  }
`;
const MessagePreview = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-start;
`;
const MessageAvatar = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: #7e22ce;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  flex-shrink: 0;
`;
const MessageInfo = styled.div`
  flex: 1;
  min-width: 0;
`;
const MessageSender = styled.div`
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.25rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const MessageTime = styled.span`
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: normal;
`;
const MessageText = styled.div`
  color: #4b5563;
  font-size: 0.875rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const MessageActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;
const MessageStatus = styled.div<{ status: 'sent' | 'delivered' | 'read' }>`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  color: ${props => {
    switch (props.status) {
      case 'sent': return '#6b7280';
      case 'delivered': return '#059669';
      case 'read': return '#7e22ce';
      default: return '#6b7280';
    }
  }};
`;

const MainDashboard = () => {
  const dashboardData = useDashboardData();
  const { profile, setProfile } = useUser();
  const navigate = useNavigate();
  // DEBUG LOGS
  const { token } = require('../../contexts/AuthContext').useAuth();
  console.log("PROFILE", profile);
  console.log("USER ID for hook", profile?._id);
  console.log("TOKEN", token);
  // Hardcode userId for testing
  const { classmates, schoolmates, teachers, loading, error } = useStudentDashboard(profile?._id);
  const [otherSchoolsSearch, setOtherSchoolsSearch] = useState('');
  const [schoolmateGrade, setSchoolmateGrade] = useState('All Grades');
  const [classmatesExpanded, setClassmatesExpanded] = useState(false);
  const [schoolmatesExpanded, setSchoolmatesExpanded] = useState(false);
  const [otherSchoolsExpanded, setOtherSchoolsExpanded] = useState(false);
  const allGrades = schoolmates ? Object.keys(schoolmates) : [];
  const [selectedSchoolmateGrade, setSelectedSchoolmateGrade] = useState(allGrades[0] || '');
  const [selectedClassmate, setSelectedClassmate] = useState<any | null>(null);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const [otherSchoolsStudents, setOtherSchoolsStudents] = useState<{ [schoolName: string]: { [grade: string]: OtherSchoolStudent[] } }>({});
  const [otherSchoolsPage, setOtherSchoolsPage] = useState(0);
  const [hasMoreStudents, setHasMoreStudents] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const STUDENTS_PER_PAGE = 4; // Number of students to show per batch
  const [selectedProfile, setSelectedProfile] = useState<StudentProfile | null>(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isEditProfileModalOpen, setIsEditProfileModalOpen] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [newPhoto, setNewPhoto] = useState<File | null>(null);
  const [tags, setTags] = useState<{ strengths: string[]; likes: string[]; hobbies: string[] }>({
    strengths: [],
    likes: [],
    hobbies: []
  });
  const [newTag, setNewTag] = useState('');
  const [activeTagField, setActiveTagField] = useState<'strengths' | 'likes' | 'hobbies' | null>(null);
  const [biography, setBiography] = useState('');
  const [activeMessageTab, setActiveMessageTab] = useState<'all' | 'unread' | 'sent'>('all');
  const [selectedUser, setSelectedUser] = useState<{ id: string; name: string } | null>(null);
  // Call History
  const [callHistory, setCallHistory] = useState<CallRecord[]>([]);
  const [selectedCall, setSelectedCall] = useState<CallRecord | null>(null);
  const [showCallModal, setShowCallModal] = useState(false);
  const [currentBatch, setCurrentBatch] = useState(1);
  const [displayedStudents, setDisplayedStudents] = useState<{ [schoolName: string]: { [grade: string]: OtherSchoolStudent[] } }>({});
  const [totalStudents, setTotalStudents] = useState(0);
  const [callError, setCallError] = useState<string | null>(null);
  const refreshCallHistory = async () => {
    await CallHistoryService.getInstance().loadCallHistory();
    setCallHistory(CallHistoryService.getInstance().getCallHistory());
  };
  useEffect(() => {
    setCallHistory(CallHistoryService.getInstance().getCallHistory());
  }, []);

  // Add handleMessageClick function
  const handleMessageClick = (student: any) => {
    setSelectedClassmate(student);
    setIsChatModalOpen(true);
  };

  // Modify the fetchOtherSchoolsStudents function to support pagination and search
  const fetchOtherSchoolsStudents = async (page: number = 1) => {
    if (!profile?._id) return;
    try {
      setIsLoadingMore(true);
      const searchParam = otherSchoolsSearch.trim() !== '' ? `&search=${encodeURIComponent(otherSchoolsSearch.trim())}` : '';
      const response = await api.get(
        `/students/other-schools?userId=${profile._id}&page=${page}&limit=${STUDENTS_PER_PAGE}${searchParam}`
      );
      const data = response.data as OtherSchoolsResponse;
      if (data.success) {
        // Use pagination info from backend if available, otherwise calculate from data
        if (data.pagination) {
          setTotalStudents(data.pagination.totalStudents);
          setHasMoreStudents(data.pagination.hasMore);
        } else {
          // Fallback to calculating from data (for backward compatibility)
          const newTotalStudents = Object.values(data.data).reduce((total, grades) => {
            return total + Object.values(grades).reduce((gradeTotal, students) => gradeTotal + students.length, 0);
          }, 0);
          setTotalStudents(newTotalStudents);
          setHasMoreStudents(newTotalStudents === STUDENTS_PER_PAGE);
        }
        
        setDisplayedStudents(data.data);
        setCurrentBatch(page);
      }
    } catch (error) {
      console.error('Error fetching other schools students:', error);
    } finally {
      setIsLoadingMore(false);
      setIsInitialLoad(false);
    }
  };

  // Load more students
  const handleLoadMore = () => {
    const nextBatch = currentBatch + 1;
    setCurrentBatch(nextBatch);
    fetchOtherSchoolsStudents(nextBatch);
  };

  // Use displayedStudents directly since search is now handled server-side
  const filteredOtherSchools = displayedStudents || {};

  // Reset current batch when search changes
  useEffect(() => {
    setCurrentBatch(1);
    fetchOtherSchoolsStudents(1);
  }, [otherSchoolsSearch]);

  // Initial load of students
  useEffect(() => {
    if (profile?._id && !isInitialLoad) {
      fetchOtherSchoolsStudents(1);
    }
  }, [profile?._id, isInitialLoad]);

  // Schoolmates filtering logic
  let filteredSchoolmates: { [grade: string]: Schoolmate[] } = {};
  let schoolmateGrades = allGrades.filter(g => g !== profile?.grade?.toString());
  if (schoolmates) {
    Object.entries(schoolmates).forEach(([grade, students]) => {
      if (grade !== profile?.grade?.toString()) {
        // Transform the students to include the school property
        let filtered = (students as RawStudent[]).map(student => ({
          _id: student._id,
          firstName: student.firstName,
          lastName: student.lastName,
          email: student.email,
          grade: student.grade,
          school: student.school || profile?.school || 'Unknown School'
        })) as Schoolmate[];
        if (filtered.length > 0) {
          filteredSchoolmates[grade] = filtered;
        }
      }
    });
  }

  // Set default selected grade for schoolmates to the first available (not user's own grade)
  React.useEffect(() => {
    if (schoolmateGrades.length > 0 && !schoolmateGrades.includes(selectedSchoolmateGrade)) {
      setSelectedSchoolmateGrade(schoolmateGrades[0]);
    }
  }, [schoolmates, profile?.grade]);

  // Add default values for dashboard data
  const {
    recentActivity = [],
    schedule = [],
    performance = { learningStreak: '0 days', averageScore: '0%' },
    recommendations = [],
    notifications = []
  } = dashboardData || {};

  console.log("classmates", classmates);
  console.log("schoolmates", schoolmates);
  console.log("teachers", teachers);
  console.log("loading", loading, "error", error);

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  // Use classmates as returned by the backend (already filtered)
  const filteredClassmatesList = classmates || [];

  // Add handleViewProfile function
  const handleViewProfile = async (student: OtherSchoolStudent) => {
    try {
      const response = await api.get(`/students/${student._id}/profile`);
      const data = response.data;
      if (data.success) {
        setSelectedProfile(data.data);
        setIsProfileModalOpen(true);
      }
    } catch (error) {
      console.error('Error fetching student profile:', error);
    }
  };

  // Add useEffect to load user's profile data
  useEffect(() => {
    if (profile?._id) {
      fetchUserProfile();
    }
  }, [profile?._id]);

  const fetchUserProfile = async () => {
    try {
      const response = await api.get(`/students/${profile?._id}/profile`);
      const data = response.data;
      if (data.success) {
        setBiography(data.data.biography || '');
        setTags({
          strengths: data.data.strengths || [],
          likes: data.data.likes || [],
          hobbies: data.data.hobbies || []
        });
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const handleSaveProfile = async () => {
    // Add check for profile ID
    if (!profile?._id) {
      console.error("Profile ID is missing, cannot save profile.");
      return;
    }
    try {
      const response = await api.put(`/students/${profile?._id}/profile`, {
        biography,
        strengths: tags.strengths,
        likes: tags.likes,
        hobbies: tags.hobbies
      });

      const data = response.data;
      console.log("Save Profile Response:", data); // Log the response
      if (data.success) {
        setIsEditProfileModalOpen(false);
        // Optionally refresh the profile data
        fetchUserProfile();
      }
    } catch (error) {
      console.error('Error updating profile:', error);
    }
  };

  const handlePhotoUpload = async () => {
    if (!newPhoto) return;
    
    // Add file validation
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
    const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif'];
    
    if (newPhoto.size > MAX_FILE_SIZE) {
      alert('File size too large. Please upload an image under 5MB.');
      return;
    }
    
    if (!ALLOWED_TYPES.includes(newPhoto.type)) {
      alert('Invalid file type. Please upload a JPEG, PNG, or GIF image.');
      return;
    }

    if (!profile?._id) {
      console.error("Profile ID is missing, cannot upload photo.");
      return;
    }

    const formData = new FormData();
    formData.append('photo', newPhoto);

    try {
      setIsUploadingPhoto(true);
      console.log("Uploading photo:", {
        photoSize: newPhoto.size,
        photoType: newPhoto.type,
        userId: profile._id,
        fileName: newPhoto.name
      });
      
      const response = await api.post(`/students/${profile?._id}/profile/photo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const data = response.data;
      console.log("Photo Upload Response:", data);
      
      if (data.success) {
        // Show success message
        alert('Profile photo uploaded successfully!');
        // Refresh the profile data
        await fetchUserProfile();
        setNewPhoto(null);
      } else {
        console.error("Upload failed:", data.message);
        alert(`Upload failed: ${data.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert('Failed to upload photo. Please try again.');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleDeletePhoto = async () => {
    // Add check for profile ID
    if (!profile?._id) {
      console.error("Profile ID is missing, cannot delete photo.");
      return;
    }
    try {
      const response = await api.delete(`/students/${profile?._id}/profile/photo`);

      const data = response.data;
      console.log("Delete Photo Response:", data); // Log the response
      if (data.success) {
        // Optionally refresh the profile data
        fetchUserProfile();
      }
    } catch (error) {
      console.error('Error deleting photo:', error);
    }
  };

  const handleAddTag = (field: 'strengths' | 'likes' | 'hobbies') => {
    if (newTag.trim() && !tags[field].includes(newTag.trim())) {
      setTags(prev => ({
        ...prev,
        [field]: [...prev[field], newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (field: 'strengths' | 'likes' | 'hobbies', tag: string) => {
    setTags(prev => ({
      ...prev,
      [field]: prev[field].filter(t => t !== tag)
    }));
  };

  const handleStartCall = async (userId: string, userName: string, userEmail: string) => {
    setCallError(null);
    setShowCallModal(true); // Show modal immediately
    try {
      // First ensure we have media access
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });
      
      // Set the stream in the call service
      const callService = CallService.getInstance();
      callService.setLocalStream(stream);
      callService.setUserId(userId);
      callService.setUserName(userName);
      
      // Start the call
      await callService.startCall(userId, userName, userEmail);
    } catch (error: any) {
      console.error('Error starting call:', error);
      // Clean up camera if call fails
      const callService = CallService.getInstance();
      callService.cleanupCall();
      setCallError(error?.message || 'Failed to start call. Please ensure you have granted camera and microphone permissions.');
    }
  };

  // Add a function to navigate to the students page
  const handleViewStudents = () => {
    navigate('/students');
  };

  return (
    <PageContainer style={{ flexDirection: 'row', minHeight: '100vh', background: '#f8fafc' }}>
      <DashboardContainer style={{ maxWidth: '100vw', width: '100vw', padding: '1.5rem 2vw' }}>
        {loading && <LoadingContainer>Loading...</LoadingContainer>}
        {error && <ErrorContainer>{error}</ErrorContainer>}
        {!loading && !error && (
          <>
            <TopBar>
              <SearchBar>
                <SearchInput type="text" placeholder="Search..." />
                <SearchIcon />
              </SearchBar>
              <UserControls>
                <IconButton>
                  <BellIcon />
                  {/* <NotificationBadge /> */}
                </IconButton>
                {/* Add Edit Profile Button */}
                <EditProfileButton onClick={() => setIsEditProfileModalOpen(true)}>
                  Edit Profile
                </EditProfileButton>
                <ProfileButton onClick={() => { /* Handle Profile Click */ }}>
                  <Avatar>{profile?.firstName?.charAt(0) || '?'}</Avatar>
                  <span>{profile?.firstName || 'User'}</span>
                </ProfileButton>
              </UserControls>
            </TopBar>
            <Header>
              <DashboardTitle>Student Dashboard</DashboardTitle>
              <WelcomeText>Welcome back, {profile?.firstName}!</WelcomeText>
              <UserInfo>
                <span>
                  <strong>Student ID:</strong> {formatSchoolId(profile?.schoolId || '', profile?._id || '')}
                </span>
                <span>
                  <strong>Grade:</strong> {profile?.grade || 'N/A'}
                </span>
                <span>
                  <strong>School:</strong> {profile?.school || 'N/A'}
                </span>
              </UserInfo>
            </Header>
            {/* Collapsible Classmates, Schoolmates, and Teachers cards */}
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap', justifyContent: 'flex-start' }}>
              {/* Classmates Card */}
              {filteredClassmatesList && filteredClassmatesList.length > 0 && (
                <Section style={{ flex: '0 1 260px', minWidth: 220, maxWidth: 300 }}>
                  <SectionHeader>
                    <SectionTitle>
                      <span role="img" aria-label="classmates">👥</span> My Classmates
                    </SectionTitle>
                  </SectionHeader>
                  <ToggleButton onClick={() => setClassmatesExpanded(e => !e)}>
                    {classmatesExpanded ? (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 15l-6-6-6 6" />
                        </svg>
                        Hide Classmates
                      </>
                    ) : (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                        View Classmates
                      </>
                    )}
                  </ToggleButton>
                  {classmatesExpanded && (
                    <StudentList>
                      {filteredClassmatesList.map((student: any) => (
                        <StudentCard key={student._id}>
                          <StudentInfo>
                            <StudentAvatar>
                              {(student.firstName || '').charAt(0)}
                            </StudentAvatar>
                            <StudentDetails>
                              <StudentName>
                                {student.firstName || ''} {student.lastName || ''}
                              </StudentName>
                              <StudentEmail>{student.email}</StudentEmail>
                            </StudentDetails>
                          </StudentInfo>
                          <StudentActions>
                            <CallButton
                              onClick={() => handleStartCall(student._id, `${student.firstName} ${student.lastName}`, student.email)}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                              </svg>
                              Call
                            </CallButton>
                            <ActionButton
                              className="primary"
                              onClick={() => {
                                setSelectedClassmate(student);
                                setIsChatModalOpen(true);
                              }}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                              </svg>
                              Chat
                            </ActionButton>
                          </StudentActions>
                        </StudentCard>
                      ))}
                    </StudentList>
                  )}
                </Section>
              )}
              {/* Schoolmates Card */}
              {schoolmates && Object.values(schoolmates).some(arr => arr.length > 0) && (
                <Section style={{ flex: '0 1 260px', minWidth: 220, maxWidth: 300 }}>
                  <SectionHeader>
                    <SectionTitle>
                      <span role="img" aria-label="schoolmates">🏫</span> My Schoolmates
                    </SectionTitle>
                  </SectionHeader>
                  <ToggleButton onClick={() => setSchoolmatesExpanded(e => !e)}>
                    {schoolmatesExpanded ? (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 15l-6-6-6 6" />
                        </svg>
                        Hide Schoolmates
                      </>
                    ) : (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                        View Schoolmates
                      </>
                    )}
                  </ToggleButton>
                  {schoolmatesExpanded && (
                    <div>
                      {schoolmateGrades.length > 0 && (
                        <select
                          value={selectedSchoolmateGrade}
                          onChange={e => setSelectedSchoolmateGrade(e.target.value)}
                          style={{
                            marginBottom: '1rem',
                            padding: '0.75rem',
                            borderRadius: '0.5rem',
                            border: '1px solid #e5e7eb',
                            width: '100%',
                            backgroundColor: 'white',
                            color: '#1f2937',
                            fontSize: '0.875rem',
                            cursor: 'pointer'
                          }}
                        >
                          {schoolmateGrades.map(grade => (
                            <option key={grade} value={grade}>Grade {grade}</option>
                          ))}
                        </select>
                      )}
                      {schoolmates && selectedSchoolmateGrade && schoolmates[selectedSchoolmateGrade] && schoolmates[selectedSchoolmateGrade].length > 0 ? (
                        <StudentList>
                          {schoolmates[selectedSchoolmateGrade]
                            .filter((s: any) => s._id !== profile?._id)
                            .map((student: any) => (
                              <StudentCard key={student._id}>
                                <StudentInfo>
                                  <StudentAvatar>
                                    {(student.firstName || '').charAt(0)}
                                  </StudentAvatar>
                                  <StudentDetails>
                                    <StudentName>
                                      {student.firstName || ''} {student.lastName || ''}
                                    </StudentName>
                                    <StudentEmail>{student.email}</StudentEmail>
                                  </StudentDetails>
                                </StudentInfo>
                                <StudentActions>
                                  <CallButton
                                    onClick={() => handleStartCall(student._id, `${student.firstName} ${student.lastName}`, student.email)}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                                    </svg>
                                    Call
                                  </CallButton>
                                  <ActionButton
                                    className="primary"
                                    onClick={() => {
                                      setSelectedClassmate(student);
                                      setIsChatModalOpen(true);
                                    }}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                                    </svg>
                                    Chat
                                  </ActionButton>
                                </StudentActions>
                              </StudentCard>
                            ))}
                        </StudentList>
                      ) : (
                        <EmptyState>No schoolmates found for this grade</EmptyState>
                      )}
                    </div>
                  )}
                </Section>
              )}
              {/* Other Schools Section - moved up and left */}
              <Section style={{ flex: '0 1 320px', minWidth: 220, maxWidth: 340, marginLeft: '-10px' }}>
                <SectionHeader>
                  <SectionTitle>Other Schools</SectionTitle>
                  <SearchBar>
                    <SearchInput
                      type="text"
                      placeholder="Search students or schools..."
                      value={otherSchoolsSearch}
                      onChange={(e) => setOtherSchoolsSearch(e.target.value)}
                    />
                  </SearchBar>
                </SectionHeader>
                {isInitialLoad ? (
                  <EmptyState>
                    Click "Load More Students" to view students from other schools
                  </EmptyState>
                ) : (
                  <>
                    {Object.entries(filteredOtherSchools).map(([schoolName, grades]) => (
                      <SchoolSection key={schoolName}>
                        <SchoolTitle>{schoolName}</SchoolTitle>
                        {Object.entries(grades).map(([grade, students]) => (
                          <GradeSection key={grade}>
                            <GradeTitle>Grade {grade}</GradeTitle>
                            <StudentGrid>
                              {students.map((student) => (
                                <StudentCard key={student._id}>
                                  <StudentInfo>
                                    <StudentName>
                                      {student.firstName} {student.lastName}
                                    </StudentName>
                                    <StudentDetails>
                                      <StudentEmail>{student.email}</StudentEmail>
                                    </StudentDetails>
                                  </StudentInfo>
                                  <ButtonGroup>
                                    <CallButton onClick={() => handleStartCall(student._id, `${student.firstName} ${student.lastName}`, student.email)}>
                                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                                      </svg>
                                      Call
                                    </CallButton>
                                    <ViewProfileButton onClick={() => handleViewProfile(student)}>
                                      View Profile
                                    </ViewProfileButton>
                                    <MessageButton onClick={() => handleMessageClick(student)}>
                                      Message
                                    </MessageButton>
                                  </ButtonGroup>
                                </StudentCard>
                              ))}
                            </StudentGrid>
                          </GradeSection>
                        ))}
                      </SchoolSection>
                    ))}
                    {Object.keys(filteredOtherSchools).length === 0 && !isLoadingMore && (
                      <EmptyState>No students from other schools found</EmptyState>
                    )}
                  </>
                )}
                {hasMoreStudents && (
                  <LoadMoreButton onClick={handleLoadMore} disabled={isLoadingMore}>
                    {isLoadingMore ? (
                      <>
                        <LoadingSpinner />
                        Loading more students...
                      </>
                    ) : (
                      isInitialLoad ? 'Load Students' : 'Load More Students'
                    )}
                  </LoadMoreButton>
                )}
              </Section>
              {/* Teachers Card */}
              {teachers && teachers.length > 0 && (
                <Section style={{ flex: 1, minWidth: 300, maxWidth: 400 }}>
                  <SectionTitle>
                    <span role="img" aria-label="teachers">👩‍🏫</span> My Teachers
                  </SectionTitle>
                  <ul>
                    {teachers.map((t: any) => (
                      <li key={t._id}>{t.firstName} {t.lastName} ({t.email})</li>
                    ))}
                  </ul>
                </Section>
              )}
            </div>
            {/* Only keep Messages, Recommendations, and Other Schools in SectionGrid */}
            <SectionGrid style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', margin: 0 }}>
              {/* Messages Section - Prioritized */}
              <Section style={{ gridColumn: '1 / -1' }}>
                <div style={{
                  background: 'white',
                  borderRadius: '0.75rem',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    padding: '1.25rem',
                    borderBottom: '1px solid #e5e7eb',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <h3 style={{
                      fontSize: '1.25rem',
                      fontWeight: 600,
                      color: '#1f2937',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <span role="img" aria-label="messages">💬</span> Messages
                    </h3>
                    <ButtonGroup>
                      <ActionButton className="primary" onClick={() => setIsChatModalOpen(true)}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        </svg>
                        New Message
                      </ActionButton>
                    </ButtonGroup>
                  </div>
                  <div style={{
                    display: 'flex',
                    gap: '0.5rem',
                    padding: '0 1.25rem',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    <button
                      onClick={() => setActiveMessageTab('all')}
                      style={{
                        padding: '0.75rem 1rem',
                        border: 'none',
                        background: 'none',
                        color: activeMessageTab === 'all' ? '#7e22ce' : '#6b7280',
                        fontWeight: activeMessageTab === 'all' ? 600 : 500,
                        cursor: 'pointer',
                        position: 'relative',
                        transition: 'all 0.2s ease',
                        borderBottom: activeMessageTab === 'all' ? '2px solid #7e22ce' : 'none'
                      }}
                    >
                      All Messages
                    </button>
                    <button
                      onClick={() => setActiveMessageTab('unread')}
                      style={{
                        padding: '0.75rem 1rem',
                        border: 'none',
                        background: 'none',
                        color: activeMessageTab === 'unread' ? '#7e22ce' : '#6b7280',
                        fontWeight: activeMessageTab === 'unread' ? 600 : 500,
                        cursor: 'pointer',
                        position: 'relative',
                        transition: 'all 0.2s ease',
                        borderBottom: activeMessageTab === 'unread' ? '2px solid #7e22ce' : 'none'
                      }}
                    >
                      Unread
                    </button>
                    <button
                      onClick={() => setActiveMessageTab('sent')}
                      style={{
                        padding: '0.75rem 1rem',
                        border: 'none',
                        background: 'none',
                        color: activeMessageTab === 'sent' ? '#7e22ce' : '#6b7280',
                        fontWeight: activeMessageTab === 'sent' ? 600 : 500,
                        cursor: 'pointer',
                        position: 'relative',
                        transition: 'all 0.2s ease',
                        borderBottom: activeMessageTab === 'sent' ? '2px solid #7e22ce' : 'none'
                      }}
                    >
                      Sent
                    </button>
                  </div>
                  <div style={{ padding: '1.25rem' }}>
                    {profile && profile._id && (
                      <MessagesList 
                        user={{ 
                          id: profile._id, 
                          name: `${profile.firstName} ${profile.lastName}`, 
                          role: 'student' 
                        }}
                      />
                    )}
                    {profile && (
                      <div style={{ marginTop: '1.5rem' }}>
                        <StudentMessaging
                          userId={profile._id || ''}
                          grade={profile.grade || ''}
                          schoolId={profile.schoolId || ''}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </Section>
              {/* Recommendations */}
              <Section>
                <SectionTitle>
                  <span role="img" aria-label="recommendations">💡</span> Recommendations
                </SectionTitle>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {recommendations && recommendations.length > 0 ? (
                    recommendations.map((rec: Recommendation, index: number) => (
                      <div key={index} style={{ padding: '0.75rem', background: '#f8fafc', borderRadius: '0.375rem' }}>
                        <div style={{ fontWeight: 500 }}>{rec.subject}</div>
                        <div style={{ color: '#64748b', fontSize: '0.875rem' }}>{rec.suggestion}</div>
                      </div>
                    ))
                  ) : (
                    <div style={{ color: '#64748b', fontSize: '0.875rem', textAlign: 'center', padding: '1rem' }}>
                      No recommendations available
                    </div>
                  )}
                </div>
              </Section>
            </SectionGrid>
            {/* Call History Section */}
            <Section>
              <SectionTitle>
                <span role="img" aria-label="call-history">📞</span> Call History
                <button style={{ marginLeft: 'auto', background: '#f3f4f6', border: 'none', borderRadius: 6, padding: '0.25rem 0.75rem', cursor: 'pointer', color: '#7e22ce', fontWeight: 600 }} onClick={refreshCallHistory}>Refresh</button>
              </SectionTitle>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {callHistory.length === 0 ? (
                  <div style={{ color: '#64748b', fontSize: '0.875rem', textAlign: 'center', padding: '1rem' }}>
                    No call history yet
                  </div>
                ) : (
                  callHistory.slice(0, 10).map((call, idx) => (
                    <div key={call.id} style={{ display: 'flex', alignItems: 'center', gap: '1rem', background: '#f8fafc', borderRadius: '0.375rem', padding: '0.75rem', cursor: 'pointer' }}
                      onClick={() => { setSelectedCall(call); setShowCallModal(true); }}
                    >
                      {call.status === 'missed' ? (
                        <FaExclamationCircle color="#ef4444" title="Missed" />
                      ) : call.callerId === profile?._id ? (
                        <FaArrowUp color="#7e22ce" title="Outgoing" />
                      ) : (
                        <FaArrowDown color="#10b981" title="Incoming" />
                      )}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 500 }}>
                          {call.callerId === profile?._id ? `To: ${call.recipientName}` : `From: ${call.callerName}`}
                        </div>
                        <div style={{ color: '#64748b', fontSize: '0.85rem' }}>
                          {call.startTime instanceof Date ? call.startTime.toLocaleString() : new Date(call.startTime).toLocaleString()}
                        </div>
                      </div>
                      <div style={{ color: call.status === 'missed' ? '#ef4444' : '#7e22ce', fontWeight: 600, minWidth: 60, textAlign: 'right' }}>
                        {call.status === 'missed' ? 'Missed' : (call.duration ? `${Math.floor(call.duration / 60)}:${(call.duration % 60).toString().padStart(2, '0')}` : '--:--')}
                      </div>
                      <button
                        style={{ background: '#10b981', color: 'white', border: 'none', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: 8, cursor: 'pointer' }}
                        title="Redial"
                        onClick={e => { e.stopPropagation(); handleStartCall(
                          call.callerId === profile?._id ? call.recipientId : call.callerId,
                          call.callerId === profile?._id ? call.recipientName : call.callerName,
                          'unknown@email.com' // You can enhance this if you store emails
                        ); }}
                      >
                        <FaPhone />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </Section>
            {/* Call Details Modal */}
            {showCallModal && selectedCall && (
              <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.3)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ background: 'white', borderRadius: 12, padding: 32, minWidth: 320, maxWidth: 400, boxShadow: '0 4px 24px rgba(30,27,75,0.12)', position: 'relative' }}>
                  <button style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', fontSize: 22, color: '#7e22ce', cursor: 'pointer' }} onClick={() => setShowCallModal(false)}>&times;</button>
                  <h3 style={{ marginBottom: 12, color: '#7e22ce' }}>Call Details</h3>
                  <div style={{ marginBottom: 8 }}><b>Type:</b> {selectedCall.status.charAt(0).toUpperCase() + selectedCall.status.slice(1)}</div>
                  <div style={{ marginBottom: 8 }}><b>Participants:</b> {selectedCall.callerName} &rarr; {selectedCall.recipientName}</div>
                  <div style={{ marginBottom: 8 }}><b>Time:</b> {selectedCall.startTime instanceof Date ? selectedCall.startTime.toLocaleString() : new Date(selectedCall.startTime).toLocaleString()}</div>
                  <div style={{ marginBottom: 8 }}><b>Duration:</b> {selectedCall.duration ? `${Math.floor(selectedCall.duration / 60)}:${(selectedCall.duration % 60).toString().padStart(2, '0')}` : '--:--'}</div>
                  <div style={{ marginBottom: 8 }}><b>Quality:</b> {selectedCall.quality?.average ? selectedCall.quality.average.toFixed(2) : 'N/A'}</div>
                  <button
                    style={{ background: '#10b981', color: 'white', border: 'none', borderRadius: 6, padding: '0.5rem 1.5rem', fontWeight: 600, marginTop: 16, cursor: 'pointer' }}
                    onClick={() => {
                      setShowCallModal(false);
                      handleStartCall(
                        selectedCall.callerId === profile?._id ? selectedCall.recipientId : selectedCall.callerId,
                        selectedCall.callerId === profile?._id ? selectedCall.recipientName : selectedCall.callerName,
                        'unknown@email.com'
                      );
                    }}
                  >
                    <FaPhone style={{ marginRight: 8 }} /> Redial
                  </button>
                </div>
              </div>
            )}
            {/* Ticket Management Section */}
            <Section style={{ marginTop: '2rem' }}>
              <SectionTitle>
                <span role="img" aria-label="tickets">🎫</span> Ticket Management
              </SectionTitle>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div style={{ 
                  background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
                  borderRadius: '0.5rem', 
                  padding: '1.5rem', 
                  color: 'white',
                  textAlign: 'center'
                }}>
                  <h3 style={{ margin: '0 0 1rem 0', color: '#ffd700' }}>Competition Tickets</h3>
                  <p style={{ margin: '0 0 1.5rem 0', opacity: 0.9 }}>
                    Purchase and manage your competition tickets. Each ticket requires a PIN for security.
                  </p>
                  <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button
                      style={{
                        background: 'linear-gradient(45deg, #4caf50, #8bc34a)',
                        border: 'none',
                        color: 'white',
                        padding: '0.75rem 1.5rem',
                        borderRadius: '25px',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '0.9rem'
                      }}
                      onClick={() => navigate('/tickets')}
                    >
                      🎫 Manage Tickets
                    </button>
                    <button
                      style={{
                        background: 'rgba(255, 255, 255, 0.1)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        color: 'white',
                        padding: '0.75rem 1.5rem',
                        borderRadius: '25px',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '0.9rem'
                      }}
                      onClick={() => navigate('/tickets/purchase')}
                    >
                      ➕ Purchase New Ticket
                    </button>
                  </div>
                </div>
              </div>
            </Section>
            
            <EmergencyButton />
          </>
        )}
      </DashboardContainer>
      {selectedClassmate && profile && (
        <MessageModal
          isOpen={isChatModalOpen}
          onClose={() => {
            setIsChatModalOpen(false);
            setSelectedClassmate(null);
          }}
          sender={{
            id: profile._id,
            name: `${profile.firstName} ${profile.lastName}`,
            role: 'student'
          }}
          receiver={{
            id: selectedClassmate._id,
            name: `${selectedClassmate.firstName} ${selectedClassmate.lastName}`,
            role: 'student'
          }}
        />
      )}
      {/* Add ViewProfileModal */}
      <ViewProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => {
          setIsProfileModalOpen(false);
          setSelectedProfile(null);
        }}
        student={selectedProfile}
      />
      {/* Add EditProfileModal */}
      {isEditProfileModalOpen && (
        <EditProfileModal onClick={() => setIsEditProfileModalOpen(false)}>
          <EditProfileContent onClick={e => e.stopPropagation()}>
            <CloseButton onClick={() => setIsEditProfileModalOpen(false)}>&times;</CloseButton>
            <h2>Edit Profile</h2>
            <Form>
              <FormGroup>
                <Label>Profile Photo</Label>
                <PhotoUploadSection>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setNewPhoto(e.target.files?.[0] || null)}
                    style={{ display: 'none' }}
                    id="photo-upload"
                  />
                  <label htmlFor="photo-upload" style={{ cursor: 'pointer' }}>
                    {newPhoto ? (
                      <img
                        src={URL.createObjectURL(newPhoto)}
                        alt="Preview"
                        style={{ width: '150px', height: '150px', objectFit: 'cover', borderRadius: '50%' }}
                      />
                    ) : (
                      <ProfilePhoto />
                    )}
                  </label>
                  <ButtonGroup>
                    <UploadButton
                      onClick={handlePhotoUpload}
                      disabled={!newPhoto || isUploadingPhoto}
                    >
                      {isUploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                    </UploadButton>
                    {profile?.profilePhoto && (
                      <DeletePhotoButton onClick={handleDeletePhoto}>
                        Delete Photo
                      </DeletePhotoButton>
                    )}
                  </ButtonGroup>
                </PhotoUploadSection>
              </FormGroup>

              <FormGroup>
                <Label>Biography</Label>
                <TextArea
                  value={biography}
                  onChange={(e) => setBiography(e.target.value)}
                  placeholder="Tell us about yourself..."
                />
              </FormGroup>

              {(['strengths', 'likes', 'hobbies'] as const).map((field) => (
                <FormGroup key={field}>
                  <Label>{field.charAt(0).toUpperCase() + field.slice(1)}</Label>
                  <TagInput>
                    {tags[field].map((tag, index) => (
                      <Tag key={index}>
                        {tag}
                        <button
                          onClick={() => handleRemoveTag(field, tag)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#6b7280',
                            marginLeft: '0.5rem',
                            cursor: 'pointer'
                          }}
                        >
                          ×
                        </button>
                      </Tag>
                    ))}
                    {activeTagField === field && (
                      <TagInputField
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddTag(field);
                          }
                        }}
                        onBlur={() => {
                          if (newTag.trim()) {
                            handleAddTag(field);
                          }
                          setActiveTagField(null);
                        }}
                        autoFocus
                      />
                    )}
                    {activeTagField !== field && (
                      <button
                        onClick={() => setActiveTagField(field)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#6b7280',
                          cursor: 'pointer',
                          padding: '0.25rem 0.5rem'
                        }}
                      >
                        + Add {field.slice(0, -1)}
                      </button>
                    )}
                  </TagInput>
                </FormGroup>
              ))}

              <SaveButton onClick={handleSaveProfile}>
                Save Changes
              </SaveButton>
            </Form>
          </EditProfileContent>
        </EditProfileModal>
      )}
      {profile && (
        <CallManager
          userId={profile._id}
          userName={`${profile.firstName} ${profile.lastName}`}
        />
      )}
      {/* Add a Students section with a link to the new page */}
      <Section>
        <SectionTitle>
          <span role="img" aria-label="students">👥</span> Students
        </SectionTitle>
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <p style={{ marginBottom: '1rem', color: '#4b5563' }}>
            View and connect with your classmates, schoolmates, and students from other schools
          </p>
          <Button 
            className="primary"
            onClick={handleViewStudents}
            style={{ minWidth: '200px' }}
          >
            View All Students
          </Button>
        </div>
      </Section>
      {/* Call Modal for Outgoing/Incoming Calls */}
      {showCallModal && profile && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.3)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: 16, minWidth: 350, minHeight: 300, boxShadow: '0 4px 24px rgba(30,27,75,0.12)', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <button style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', fontSize: 22, color: '#7e22ce', cursor: 'pointer' }} onClick={() => { setShowCallModal(false); setCallError(null); CallService.getInstance().cleanupCall(); }}>&times;</button>
            {callError ? (
              <div style={{ color: '#ef4444', textAlign: 'center', padding: '2rem' }}>
                <h3>Call Failed</h3>
                <p>{callError}</p>
                <button style={{ marginTop: 16, background: '#7e22ce', color: 'white', border: 'none', borderRadius: 6, padding: '0.5rem 1.5rem', fontWeight: 600, cursor: 'pointer' }} onClick={() => { setShowCallModal(false); setCallError(null); CallService.getInstance().cleanupCall(); }}>Close</button>
              </div>
            ) : (
              <CallManager userId={profile._id} userName={`${profile.firstName} ${profile.lastName}`} onCallEnd={() => { setShowCallModal(false); setCallError(null); }} />
            )}
          </div>
        </div>
      )}
    </PageContainer>
  );
};

export default MainDashboard;

// Add new styled components
const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #e5e7eb;
`;

const GradeSection = styled.div`
  margin-bottom: 2rem;
`;

const GradeTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 1rem;
`;

const StudentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
`;

const StudentCard = styled.div`
  background: white;
  border-radius: 0.75rem;
  padding: 1.25rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  border: 1px solid #e5e7eb;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    border-color: #7e22ce;
  }
`;

const StudentInfo = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const StudentAvatar = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: #7e22ce;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1rem;
`;

const StudentDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const StudentName = styled.div`
  font-weight: 600;
  color: #1f2937;
  font-size: 1rem;
`;

const StudentEmail = styled.div`
  color: #6b7280;
  font-size: 0.875rem;
`;

const StudentActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const StudentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 400px;
  overflow-y: auto;
  padding-right: 0.5rem;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`;

const SchoolSection = styled.div`
  margin-bottom: 2rem;
  background: white;
  border-radius: 0.5rem;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const SchoolTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e5e7eb;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: #6b7280;
  background: #f9fafb;
  border-radius: 0.5rem;
  margin: 1rem 0;
`;

const LoadMoreButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 1rem;
  margin-top: 1.5rem;
  background: #7e22ce;
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background: #6b21a8;
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

const LoadingSpinner = styled.div`
  width: 1.25rem;
  height: 1.25rem;
  border: 2px solid #ffffff;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// Add ViewProfileModal component
const ViewProfileModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  student: StudentProfile | null;
}> = ({ isOpen, onClose, student }) => {
  if (!isOpen || !student) return null;

  console.log("Viewing student profile:", student);
  console.log("Profile photo URL:", student.profilePhoto);

  return (
    <ProfileModal onClick={onClose}>
      <ProfileModalContent onClick={e => e.stopPropagation()}>
        <CloseButton onClick={onClose}>&times;</CloseButton>
        <ProfileHeader>
          <ProfilePhoto src={student.profilePhoto || 'placeholder.jpg'} alt="Profile" />
          <ProfileInfo>
            <ProfileName>{student.firstName} {student.lastName}</ProfileName>
            <ProfileSchool>{student.schoolName} • Grade {student.grade}</ProfileSchool>
            {student.careerAspiration && (
              <ProfileSection>
                <SectionTitle>Career Aspiration</SectionTitle>
                <p>{student.careerAspiration}</p>
              </ProfileSection>
            )}
          </ProfileInfo>
        </ProfileHeader>

        {student.biography && (
          <ProfileSection>
            <SectionTitle>About</SectionTitle>
            <p>{student.biography}</p>
          </ProfileSection>
        )}

        {student.cbcCompetencies && student.cbcCompetencies.length > 0 && (
          <ProfileSection>
            <SectionTitle>CBC Competencies</SectionTitle>
            <TagList>
              {student.cbcCompetencies.map((competency, index) => (
                <Tag key={index}>{competency}</Tag>
              ))}
            </TagList>
          </ProfileSection>
        )}

        {student.skills && student.skills.length > 0 && (
          <ProfileSection>
            <SectionTitle>Skills</SectionTitle>
            <TagList>
              {student.skills.map((skill, index) => (
                <Tag key={index}>{skill}</Tag>
              ))}
            </TagList>
          </ProfileSection>
        )}

        {student.strengths && student.strengths.length > 0 && (
          <ProfileSection>
            <SectionTitle>Strengths</SectionTitle>
            <TagList>
              {student.strengths.map((strength, index) => (
                <Tag key={index}>{strength}</Tag>
              ))}
            </TagList>
          </ProfileSection>
        )}

        {student.likes && student.likes.length > 0 && (
          <ProfileSection>
            <SectionTitle>Likes</SectionTitle>
            <TagList>
              {student.likes.map((like, index) => (
                <Tag key={index}>{like}</Tag>
              ))}
            </TagList>
          </ProfileSection>
        )}

        {student.hobbies && student.hobbies.length > 0 && (
          <ProfileSection>
            <SectionTitle>Hobbies</SectionTitle>
            <TagList>
              {student.hobbies.map((hobby, index) => (
                <Tag key={index}>{hobby}</Tag>
              ))}
            </TagList>
          </ProfileSection>
        )}

        {student.extracurricularActivities && student.extracurricularActivities.length > 0 && (
          <ProfileSection>
            <SectionTitle>Extracurricular Activities</SectionTitle>
            <TagList>
              {student.extracurricularActivities.map((activity, index) => (
                <Tag key={index}>{activity}</Tag>
              ))}
            </TagList>
          </ProfileSection>
        )}

        {student.projects && student.projects.length > 0 && (
          <ProfileSection>
            <SectionTitle>Projects</SectionTitle>
            <ul>
              {student.projects.map((project, index) => (
                <li key={index}>
                  <strong>{project.name}</strong>: {project.description} {project.url && <a href={project.url} target="_blank" rel="noopener noreferrer">(Link)</a>}
                  {project.competenciesCovered && project.competenciesCovered.length > 0 && (
                    <span> (Competencies: {project.competenciesCovered.join(', ')})</span>
                  )}
                </li>
              ))}
            </ul>
          </ProfileSection>
        )}
      </ProfileModalContent>
    </ProfileModal>
  );
};

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  background: #7e22ce;
  color: white;

  &:hover {
    background: #6b21a8;
  }

  &.primary {
    background: #7e22ce;
    color: white;
    &:hover {
      background: #6b21a8;
    }
  }

  &.secondary {
    background: #f3f4f6;
    color: #4b5563;
    &:hover {
      background: #e5e7eb;
    }
  }
`;